import React, { useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";

import shareIcon from "../../../assets/images/share.png";
import closeIcon from "../../../assets/images/close.png";
import arrowIconWhite from "../../../assets/images/arrowIconWhite.png";
import Illustrazione from "../../../assets/images/icona-pag8.svg";
import LogoReale from "../../../assets/images/Logo_reale-foundation 1.png";
import Approfondimento from "./en-sezione-4-popup-approfondimento";

/* ===================== DATA (solo ciò che serve) ===================== */
const KPI_TOP = [
  { id: "soci",  label: "Number of Members/Customers", value: 5182900, unit: null, note: null, decimals: 0 },
  { id: "vant",  label: "Mutual benefits",             value: 32.7,    unit: null, note: "million", decimals: 1 },
  { id: "dip",   label: "Number of employees",         value: 4584,    unit: null, note: null, decimals: 0 },
];

const onShare = (popupId) => {
    const url = new URL(window.location.href);
    url.searchParams.set("popup", popupId);

    if (navigator.share) {
      navigator
        .share({ title: "Commitment in favour of people", url: url.toString() })
        .catch(() => {});
    } else if (navigator.clipboard) {
      navigator.clipboard.writeText(url.toString()).catch(() => {});
    }
};

// Card Foundation
const FOUNDATION = {
  beneficiari: 455277,
  erogazioni: 2.79, // million
};

/* ===================== COUNTER ===================== */
function useInView(ref, rootMargin = "0px") {
  const [inView, set] = useState(false);
  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    const obs = new IntersectionObserver(
      ([e]) => e.isIntersecting && set(true),
      { root: null, rootMargin, threshold: 0.2 }
    );
    obs.observe(el);
    return () => obs.disconnect();
  }, [ref, rootMargin]);
  return inView;
}

function formatNumber(val, decimals, unit) {
  if (val == null) return "—";
  const n = Number(val);
  const fmt = n.toLocaleString("en-US", {
    minimumFractionDigits: decimals ?? 0,
    maximumFractionDigits: decimals ?? 0,
    useGrouping: "always",
  });
  if (unit === "eur_mln") return `€${fmt}`;
  if (unit === "eur_abs") return `€${fmt}`;
  return fmt;
}

function Counter({ value, decimals = 0, unit = null, duration = 1200, className = "" }) {
  const ref = useRef(null);
  const inView = useInView(ref, "0px");
  const [display, setDisplay] = useState(value == null ? "—" : formatNumber(0, decimals, unit));

  useEffect(() => {
    if (!inView || value == null) return;
    let raf, start;
    const from = 0;
    const to = Number(value);
    const easeOut = (t) => 1 - Math.pow(1 - t, 3);
    const step = (ts) => {
      if (!start) start = ts;
      const p = Math.min(1, (ts - start) / duration);
      const eased = easeOut(p);
      const curr = from + (to - from) * eased;
      setDisplay(formatNumber(curr, decimals, unit));
      if (p < 1) raf = requestAnimationFrame(step);
    };
    raf = requestAnimationFrame(step);
    return () => cancelAnimationFrame(raf);
  }, [inView, value, decimals, unit, duration]);

  return <div ref={ref} className={className}>{display}</div>;
}

/* ===================== SUB-COMPONENTS ===================== */
function KpiCardTop({ kpi }) {
  return (
    <div className="flex flex-col gap-1">
      <div className="text-[#0E3761] text-[16px] md:text-[22px] leading-snug font-bold">
        {kpi.label}
      </div>
      <div className="flex items-baseline gap-2">
        <Counter
          value={kpi.value}
          decimals={kpi.decimals ?? 0}
          unit={kpi.unit}
          className="text-[#0961C9] text-[40px] md:text-[40px] font-bold"
        />
        {kpi.note && <span className="text-[#0E3761] text-[14px] md:text-[16px]">{kpi.note}</span>}
      </div>
    </div>
  );
}

function FoundationCard() {
  const [open1, setOpen1] = useState(false);
  return (
    <>
      <div className="rounded-[28px] bg-[#E1E8F4] p-6 md:p-8">
        {/* Header: logo + Visit the website */}
        <div className="flex flex-col md:flex-row items-center justify-between gap-4">
          <img src={LogoReale} className="h-[40px] md:h-[52px] w-auto" alt="Reale Foundation" />
          <a href="https://realefoundation.org/" target="_blank" rel="noreferrer">
            <button
              type="button"
              className="bt-secondary2 flex items-center gap-2"
              onClick={(e) => e.stopPropagation()}
            >
              <img src={arrowIconWhite} alt="" className="w-[22px] h-[22px]" />
              Visit the website
            </button>
          </a>
        </div>

        {/* Body: 2 colonne */}
        <div className="mt-6 flex flex-col md:flex-row gap-6 md:gap-10">
          {/* Beneficiari */}
          <div className="flex-1 md:border-r-[3px] md:border-r-white md:pr-8">
            <div className="text-[#0E3761] text-[18px] md:text-[24px] leading-snug font-bold">
              Beneficiaries of Reale Foundation initiative
            </div>
            <div className="text-[#0961C9] text-[40px] md:text-[44px] font-bold mt-2">
              <Counter value={FOUNDATION.beneficiari} />
            </div>
            <div className="text-[#0E3761] text-[14px] md:text-[16px]">beneficiaries (direct and indirect)</div>
          </div>

          {/* Erogazioni */}
          <div className="flex-1">
            <div className="text-[#0E3761] text-[18px] md:text-[24px] leading-snug font-bold">
              Disbursements of Reale Foundation in favour of charitable initiatives
            </div>
            <div className="mt-2 flex items-baseline gap-2">
              <div className="text-[#0961C9] text-[40px] md:text-[44px] font-bold">
                <Counter value={FOUNDATION.erogazioni} decimals={2} />
              </div>
              <span className="text-[#0E3761] text-[14px] md:text-[16px]">million</span>
            </div>
            <button
              type="button"
              className="bt-secondary2 flex items-center gap-2 mt-4"
              onClick={(e) => { e.stopPropagation(); setOpen1(true); }}
            >
              <img src={arrowIconWhite} alt="" className="w-[22px] h-[22px]" />
              Find out more
            </button>
          </div>
        </div>
      </div>

      {/* Sotto-popup */}
      <Approfondimento isOpen={open1} onClose={() => setOpen1(false)} />
    </>
  );
}

/* ===================== MAIN POPUP ===================== */
export default function PopupImpegnoPersone({ isOpen, onClose }) {
  const overlayRef = useRef(null);

  useEffect(() => {
    const onKey = (e) => e.key === "Escape" && onClose?.();
    if (isOpen) {
      document.body.classList.add("overflow-hidden");
      window.addEventListener("keydown", onKey);
    }
    return () => {
      document.body.classList.remove("overflow-hidden");
      window.removeEventListener("keydown", onKey);
    };
  }, [isOpen, onClose]);

  if (!isOpen) return null;

  return createPortal(
    <div
      ref={overlayRef}
      className="fixed inset-0 z-[999] bg-black/50 backdrop-blur-[2px] flex items-center justify-center p-3 md:p-6"
      onMouseDown={(e) => { if (e.target === overlayRef.current) onClose?.(); }}
      role="dialog"
      aria-modal="true"
      aria-label="Our figures — Commitment in favour of people"
    >
      <div
        className="relative w-full md:w-[1280px] shadow-2xl max-h-[90vh] overflow-y-auto overscroll-contain"
        style={{ background: "#F1F7FD" }}
        onMouseDown={(e) => e.stopPropagation()}
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header share/close */}
        <div className="sticky top-3 z-10 flex items-center gap-2 justify-end pr-3">
          <button
            type="button"
            className="inline-flex h-9 w-9 items-center justify-center rounded-full bg-white/90 border border-[#0E3761]/30 hover:bg-white transition"
            title="Condividi" aria-label="Condividi"
            onClick={() => onShare("PopupImpegnoPersone")}
          >
            <img src={shareIcon} alt="" className="h-4 w-4" />
          </button>
          <button
            type="button"
            onClick={(e) => { e.stopPropagation(); onClose?.(); }}
            className="inline-flex h-9 w-9 items-center justify-center rounded-full bg-white/90 border border-[#0E3761]/30 hover:bg-white transition"
            title="Chiudi" aria-label="Chiudi"
          >
            <img src={closeIcon} alt="Chiudi" className="h-4 w-4" />
          </button>
        </div>

        {/* Content */}
        <div className="p-6 md:p-8">
          <h2 className="text-[#0E3761] text-[30px] md:text-[40px] font-bold">Our figures</h2>
          <p className="text-[#0E3761] text-[18px] md:text-[22px] mt-1 mb-0">Commitment in favour of people</p>
          <p className="text-[#0E3761] text-[12px] md:text-[14px] mt-0 italic">*As at 31st December 2025</p>

          {/* DESKTOP */}
          <div className="hidden md:block mt-8">
            {/* card bianca */}
            <div className="relative overflow-hidden bg-white rounded-[32px] p-10 border-2 border-[#b1c4e8]">
              {/* blob azzurro in basso a sinistra, dietro l'illustrazione, clippato dalla card */}
              <div className="absolute bottom-[-100px] left-[-140px] w-[440px] h-[480px] rounded-full bg-[#DDEDFA] z-0" />
              <img src={Illustrazione} alt="" className="absolute left-[-100px] bottom-[-50px] w-[400px]" />

              <div className="relative z-[1] flex gap-10">
                {/* sinistra: illustrazione in fondo */}
                <div className="w-1/4 min-w-[260px] flex flex-col justify-end">

                </div>

                {/* destra: KPI sopra + Foundation */}
                <div className="w-3/4 flex flex-col gap-8">
                  {/* 3 KPI top */}
                  <div className="grid grid-cols-3 gap-10">
                    {KPI_TOP.map((k) => (
                      <KpiCardTop key={k.id} kpi={k} />
                    ))}
                  </div>

                  {/* Card Foundation */}
                  <FoundationCard />
                </div>
              </div>
            </div>
          </div>

          {/* MOBILE / TABLET */}
          <div className="block md:hidden mt-4">

            {/* Card Foundation */}
            <div className="mb-8">
              <FoundationCard />
            </div>

            {/* Illustrazione */}
            <div className="hidden mb-8">
              <img src={Illustrazione} alt="" className="w-[60%]" />
            </div>

            {/* KPI impilati */}
            <div className="space-y-6">
              {KPI_TOP.map((k) => (
                <div key={k.id}>
                  <div className="text-[#0E3761] text-[18px] font-semibold leading-snug">{k.label}</div>
                  <div className="flex items-baseline gap-2">
                    <Counter
                      value={k.value}
                      decimals={k.decimals ?? 0}
                      unit={k.unit}
                      duration={900}
                      className="text-[#0961C9] text-[50px] md:text-[34px] font-bold"
                    />
                    {k.note && <span className="text-[#0E3761] text-[14px]">{k.note}</span>}
                  </div>
                </div>
              ))}
            </div>

          </div>

          {/* CTA finale opzionale */}
          <div className="mt-8">
            <a href="https://www.realegroup.eu/groupshare/0/EN/ListaReportsAndAccounts/64/Reale%20Group_Integrated%20Consolidated%20Financial%20Statement%202025.pdf#page=23" target="_blank">
              <button
                type="button"
                className="bt-secondary2 text-sm px-4 py-2"
              >
                <img src={arrowIconWhite} alt="Arrow" className="inline-block mr-2 w-[20px] h-[20px]" />
                 Consult the Report – chapter 1 • par. 1.1.3.3
              </button>
            </a>
          </div>
        </div>
      </div>
    </div>,
    document.body
  );
}
